import React, { useState, useEffect, useMemo } from "react";
import {
  Card,
  CardContent,
  Typography,
  Box,
  Snackbar,
  Alert,
} from "@mui/material";
import { PieChart, Pie, Cell, Legend, Tooltip } from "recharts";
import Calendar from "react-calendar";
import "react-calendar/dist/Calendar.css";
import { getData } from "../../services/FetchNodeservices"; // ✅ add

// Holidays JSON
const holidays = {
  "2025-01-01": "New Year's Day",
  "2025-01-26": "Republic Day",
  "2025-02-26": "Shivratri",
  "2025-03-14": "Holi",
  "2025-03-15": "Bhai Dooj",
  "2025-08-09": "Raksha Bandhan",
  "2025-08-15": "Independence Day",
  "2025-08-16": "Janmashtami",
  "2025-10-02": "Gandhi Jayanti & Dusshera",
  "2025-10-20": "Diwali",
  "2025-10-22": "Goverdhan Pooja",
  "2025-10-23": "Bhai Dooj",
};

const months = [
  "January","February","March","April","May","June",
  "July","August","September","October","November","December"
];

// chart dummy
const totalLeaves = 22;
const leaveTaken = 15;
const remainingLeaves = totalLeaves - leaveTaken;

const chartData = [
  { name: "Leave Taken", value: leaveTaken, color: "#f06292" },
  { name: "Remaining Leaves", value: remainingLeaves, color: "#42a5f5" },
  { name: "National Holidays", value: 6, color: "#fdd835" },
];

const emojiMap = {
  "Leave Taken": "🩷",
  "Remaining Leaves": "🔵",
  "National Holidays": "🎉",
};

const CustomTooltip = ({ active, payload }) => {
  if (active && payload && payload.length) {
    const { name, value } = payload[0];
    return (
      <div
        style={{
          backgroundColor: "#000",
          color: "#fff",
          padding: "8px 12px",
          borderRadius: "8px",
          border: "1px solid #444",
          fontSize: "0.9rem",
        }}
      >
        <strong>
          {emojiMap[name]} {name}
        </strong>
        <br />
        <span>{value}</span>
      </div>
    );
  }
  return null;
};

const LeaveCalendar = () => {
  const user = JSON.parse(localStorage.getItem("USER"));

  const [selectedDate, setSelectedDate] = useState(new Date());
  const [popup, setPopup] = useState({ open: false, message: "", success: false });

  const [leaves, setLeaves] = useState([]);

  // Format date as YYYY-MM-DD
  const formatDateKey = (date) =>
    `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}-${String(
      date.getDate()
    ).padStart(2, "0")}`;

  // ✅ Build leaveMap with dates
  const leaveMap = useMemo(() => {
    const map = {};
    leaves.forEach((lv) => {
      const start = new Date(lv.date_from);
      const end = new Date(lv.date_to);

      for (let d = new Date(start); d <= end; d.setDate(d.getDate() + 1)) {
        const k = formatDateKey(d);
        map[k] = lv.status; // Approved / Rejected / Pending
      }
    });
    return map;
  }, [leaves]);

  // ✅ fetch leaves
  const fetchLeaves = async () => {
    if (!user?.id) return;
    try {
      const res = await getData(`leave/my-leaves/${user.id}`);
      if (res?.status) setLeaves(res.data || []);
    } catch (err) {
      console.error(err);
    }
  };

  useEffect(() => {
    fetchLeaves();
    // eslint-disable-next-line
  }, []);

  // ✅ tile class
  const tileClassName = ({ date }) => {
    const dateKey = formatDateKey(date);
    const dayOfWeek = date.getDay();

    if (holidays[dateKey]) return "holiday";
    if (leaveMap[dateKey] === "Approved") return "leaveApproved";
    if (leaveMap[dateKey] === "Rejected") return "leaveRejected";
    if (dayOfWeek === 0) return "sunday";
    if (dayOfWeek === 6) return "saturday";
    return "";
  };

  // ✅ tile content (cross for rejected)
  const tileContent = ({ date }) => {
    const dateKey = formatDateKey(date);

    if (leaveMap[dateKey] === "Rejected") {
      return (
        <div style={{ fontSize: "12px", fontWeight: "bold", color: "#ff3b3b" }}>
          ❌
        </div>
      );
    }
    if (leaveMap[dateKey] === "Approved") {
      return (
        <div style={{ fontSize: "12px", fontWeight: "bold", color: "#00e676" }}>
          ●
        </div>
      );
    }
    return null;
  };

  // popup click
  const handleDayClick = (date) => {
    const dateKey = formatDateKey(date);
    const dayOfWeek = date.getDay();

    let message = `${date.getDate()} ${months[date.getMonth()]} ${date.getFullYear()}`;
    let isSuccess = false;

    if (holidays[dateKey]) {
      message += `\n🎉 ${holidays[dateKey]}`;
      isSuccess = true;
    } else if (leaveMap[dateKey] === "Approved") {
      message += `\n✅ Approved Leave`;
      isSuccess = true;
    } else if (leaveMap[dateKey] === "Rejected") {
      message += `\n❌ Rejected Leave`;
    } else if (leaveMap[dateKey] === "Pending") {
      message += `\n🟡 Pending Leave`;
    } else if (dayOfWeek === 0) {
      message += `\n🛌 Sunday`;
    } else if (dayOfWeek === 6) {
      message += `\n🕒 Saturday`;
    } else {
      message += `\n📅 Working Day`;
    }

    setPopup({ open: true, message, success: isSuccess });
  };

  useEffect(() => {
    handleDayClick(new Date());
    // eslint-disable-next-line
  }, []);

  return (
    <Card sx={{ backgroundColor: "#111", color: "white", borderRadius: 3, p: 2, width: "44%" }}>
      <CardContent>
        <Typography variant="h6" gutterBottom>
          Leave Calendar
        </Typography>

        {/* Donut Chart */}
        <Box display="flex" justifyContent="center" mb={3}>
          <PieChart width={280} height={250}>
            <Pie
              data={chartData}
              cx="50%"
              cy="50%"
              innerRadius={60}
              outerRadius={100}
              paddingAngle={3}
              dataKey="value"
            >
              {chartData.map((entry, index) => (
                <Cell key={`cell-${index}`} fill={entry.color} />
              ))}
            </Pie>
            <Tooltip content={<CustomTooltip />} />
            <Legend
              verticalAlign="bottom"
              height={36}
              formatter={(value) => (
                <span style={{ color: "white", fontSize: "0.9rem" }}>{value}</span>
              )}
            />
          </PieChart>
        </Box>

        {/* Calendar */}
        <Box
          sx={{
            "& .react-calendar": {
              background: "#1e1e1e",
              border: "none",
              borderRadius: "12px",
              padding: "10px",
            },
            "& .react-calendar__navigation button": {
              color: "white",
              fontSize: "1.2rem",
              fontWeight: "bold",
            },
            "& .react-calendar__month-view__weekdays": {
              textTransform: "uppercase",
              fontSize: "0.9rem",
              color: "white",
              fontWeight: "bold",
            },
            "& .react-calendar__tile": {
              borderRadius: "8px",
              textAlign: "center",
              padding: "10px 0",
              color: "white",
            },

            "& .holiday": { background: "#db3434 !important" },
            "& .sunday": { background: "#db3434 !important" },
            "& .saturday": { background: "#fdd835 !important", color: "black !important" },

            // ✅ approved leave highlight
            "& .leaveApproved": { background: "#1b5e20 !important" },

            // ✅ rejected leave highlight
            "& .leaveRejected": { background: "#7f0000 !important" },

            display: "flex",
            justifyContent: "center",
          }}
        >
          <Calendar
            value={selectedDate}
            onChange={setSelectedDate}
            tileClassName={tileClassName}
            tileContent={tileContent}
            onClickDay={handleDayClick}
            formatShortWeekday={(locale, date) => ["S", "M", "T", "W", "T", "F", "S"][date.getDay()]}
            formatMonthYear={(locale, date) =>
              date.toLocaleDateString("en-US", { month: "long", year: "numeric" })
            }
            formatDay={(locale, date) => date.getDate()}
          />
        </Box>
      </CardContent>

      {/* Snackbar */}
      <Snackbar
        open={popup.open}
        autoHideDuration={3000}
        onClose={() => setPopup({ ...popup, open: false })}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert severity={popup.success ? "success" : "info"} sx={{ whiteSpace: "pre-line" }}>
          {popup.message}
        </Alert>
      </Snackbar>
    </Card>
  );
};

export default LeaveCalendar;
